import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getUserData } from "./UserAPI";
import APP_CONFIG from "../app/configuration/AppConfig";
import { IUserData, IUserDataDto, UserAccessType, UserInitializationState } from "./Models";
import { RootState } from "../app/Store";

interface IAccountData {
  accountName: string;
  oid?: string;
}
interface IFetchUserDataResponse {
  siteIds: string[];
  userType: UserAccessType;
  userName: string;
  oid?: string;
}
const initialState: IUserData = {
  siteIds: [],
  userType: UserAccessType.None,
  userName: "",
  initializationState: UserInitializationState.NotInitialized,
  sudoMode: false,
  oid: undefined,
};
const NoChangeInUserData = "No change in user data";
export const userSlice = createSlice({
  name: "user",
  initialState: initialState,
  reducers: {
    setSudoMode: (state: IUserData, action: PayloadAction<boolean>) => {
      state.sudoMode = action.payload;
    },
    reset: () => {
      return initialState;
    },
    impersonateAs: (state: IUserData, action: PayloadAction<UserAccessType>) => {
      state.originalUserType =
        action.payload === state.originalUserType
          ? undefined
          : (state.originalUserType ?? state.userType);
      state.userType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }
        Object.assign(state, action.payload);
        state.initializationState = UserInitializationState.Initialized;
        delete state.activeRequestId;
        delete state.initializationStateBeforeRequest;
      })
      .addCase(fetchUserData.pending, (state, action) => {
        if (state.initializationState !== UserInitializationState.Initializing) {
          state.initializationStateBeforeRequest = state.initializationState;
        }
        state.activeRequestId = action.meta.requestId;
        state.initializationState = UserInitializationState.Initializing;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }
        delete state.activeRequestId;
        if (action.payload === NoChangeInUserData) {
          state.initializationState = UserInitializationState.Initialized;
          delete state.initializationStateBeforeRequest;
          return;
        }
        if (action.meta.aborted) {
          state.initializationState =
            state.initializationStateBeforeRequest ?? UserInitializationState.NotInitialized;
          delete state.initializationStateBeforeRequest;
          return;
        }
        delete state.initializationStateBeforeRequest;
        state.initializationState = UserInitializationState.NotInitialized;
        console.error(action.error);
      });
  },
});
export const fetchUserData = createAsyncThunk(
  "user/fetchUserData",
  async (account: IAccountData, { signal, rejectWithValue, getState }) => {
    while (!signal.aborted) {
      try {
        const userDataDto = await getUserData(signal);
        const state = getState() as RootState;
        if (isUserDataEqual(userDataDto, account, state.user)) {
          return rejectWithValue(NoChangeInUserData);
        }
        return {
          ...userDataDto,
          userName: account.accountName,
          oid: account.oid,
        } as IFetchUserDataResponse;
      } catch (e) {
        if (signal.aborted) {
          return rejectWithValue(e);
        }
        await new Promise((resolve, reject) => {
          setTimeout(resolve, APP_CONFIG.milliSecBetweenFailedInitRequests);
          signal.addEventListener("abort", () => reject(new Error("aborted")));
        });
      }
    }
    return rejectWithValue({ message: "aborted" });
  }
);

const isUserDataEqual = (
  newUserDataDto: IUserDataDto,
  newAccount: IAccountData,
  currentUserData: IUserData
): boolean => {
  return (
    JSON.stringify(newUserDataDto.siteIds) === JSON.stringify(currentUserData.siteIds) &&
    newUserDataDto.userType === currentUserData.userType &&
    newAccount.accountName === currentUserData.userName &&
    newAccount.oid === currentUserData.oid
  );
};
export const selectUserSiteIds = (state: RootState) => state.user.siteIds;

export const userReducer = userSlice.reducer;
